"use client";
import Icon from "components/commons/Icon";
import { motion } from "framer-motion";
import { useOutsideAlerter } from "hooks/OutsideAlerter";
import { useRouter } from "next/router";
import { useCallback, useRef, useState } from "react";
import { hasLocalizedRoute, localizePath } from "src/utils/localizedRoutes";
import English from "@/assets/img/language/en.svg";
import Spanish from "@/assets/img/language/es.svg";
import French from "@/assets/img/language/fr.svg";
import Portuguese from "@/assets/img/language/pt.svg";

// Declared at module scope (not inside LangSwitcher) so React keeps the same
// component identity across renders instead of remounting each flag.
function Flag({ src, alt, lang, className, onSelect }: any) {
  return (
    <div className={className} onClick={() => onSelect(lang)}>
      <Icon src={src} alt={alt} width={28} height={28} />
    </div>
  );
}

export default function LangSwitcher() {
  const router = useRouter();
  const { pathname, asPath, query, locale } = router;
  const [isMouse, toggleMouse] = useState(false);

  const toggleMouseMenu = () => {
    toggleMouse(!isMouse);
  };

  const subMenuAnimate = {
    enter: {
      opacity: 1,
      rotateX: 0,
      transition: {
        duration: 0.5,
      },
      display: "block",
    },
    exit: {
      opacity: 0,
      rotateX: -15,
      transition: {
        duration: 0.2,
      },
      transitionEnd: {
        display: "none",
      },
    },
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter(wrapperRef, () => toggleMouse(false));

  const switchLanguage = useCallback(
    (lang: false | string) => {
      // Persist the explicit choice. next.config.ts has localeDetection on, so
      // without this cookie a visitor whose browser is (say) English would be
      // redirected straight back to /en the next time they hit an unprefixed
      // URL. Next.js checks NEXT_LOCALE before Accept-Language, so writing it
      // here makes the picked language stick.
      if (lang) {
        document.cookie = `NEXT_LOCALE=${lang}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax${
          window.location.protocol === "https:" ? "; secure" : ""
        }`;
      }
      // router.pathname is the pt-worded file route, while asPath is the URL
      // currently in the address bar -- i.e. the slug of the locale we are
      // leaving. Reusing it would carry that slug into the new locale
      // (/fr/termes-et-conditions -> /en/termes-et-conditions, which matches no
      // rewrite and 404s), so routes with a translated slug get the target
      // locale's path instead. Everything else keeps asPath, which already
      // carries any resolved dynamic segments (e.g. /comprar/<id>).
      const as = hasLocalizedRoute(pathname)
        ? localizePath(pathname, lang || undefined)
        : asPath;
      return router.push({ pathname, query }, as, { locale: lang });
    },
    [router, pathname, query, asPath],
  );

  return (
    <div className="cursor-pointer" onClick={toggleMouseMenu} ref={wrapperRef}>
      <div className="flex lg:flex-col">
        <motion.div className="flex">
          {!locale ||
            (locale === "pt" && (
              <Icon src={Portuguese} alt="Portuguese" width={28} height={28} />
            ))}
          {locale === "en" && (
            <Icon src={English} alt="English" width={28} height={28} />
          )}
          {locale === "es" && (
            <Icon src={Spanish} alt="Spanish" width={28} height={28} />
          )}
          {locale === "fr" && (
            <Icon src={French} alt="French" width={28} height={28} />
          )}
        </motion.div>
        <motion.div
          initial="enter"
          animate={isMouse ? "enter" : "exit"}
          variants={subMenuAnimate}
          className="absolute top-12"
        >
          <div className="-mb-1.5 pb-3.5 space-y-4">
            {isMouse && locale !== "pt" && (
              <Flag
                src={Portuguese}
                alt="Portuguese"
                lang="pt"
                onSelect={switchLanguage}
              />
            )}
            {isMouse && locale !== "en" && (
              <Flag
                src={English}
                alt="English"
                lang="en"
                onSelect={switchLanguage}
              />
            )}
            {isMouse && locale !== "es" && (
              <Flag
                src={Spanish}
                alt="Spanish"
                lang="es"
                onSelect={switchLanguage}
              />
            )}
            {isMouse && locale !== "fr" && (
              <Flag
                src={French}
                alt="French"
                lang="fr"
                onSelect={switchLanguage}
              />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
